import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { BACKEND_URL, clearAdminAuth, setCsrfToken } from "../lib/api";
import { encode } from "blurhash";
import { waLink } from "../lib/whatsapp";
import {
  Bell,
  MessageCircle,
  Upload,
  RefreshCw,
  Search,
  ArrowRight,
  Plus,
  Gift,
  Package,
  ClipboardList,
  Truck,
  IndianRupee,
  Clock3,
} from "lucide-react";
import AdminNotifications from "../components/admin/AdminNotifications";
import AdminOrders from "../components/admin/AdminOrders";
import AdminSales from "../components/admin/AdminSales";
import AdminCustomOrders from "../components/admin/AdminCustomOrders";
import AdminDesigns from "../components/admin/AdminDesigns";
import AdminProducts from "../components/admin/AdminProducts";
import AdminProductBulkImport from "../components/admin/AdminProductBulkImport";
import AdminSizeCharts from "../components/admin/AdminSizeCharts";
import AdminOffers from "../components/admin/AdminOffers";
import AdminSettings from "../components/admin/AdminSettings";
import AdminDashboardActionCenter from "../components/admin/AdminDashboardActionCenter";
import AdminDashboardSalesPerformance from "../components/admin/AdminDashboardSalesPerformance";

const TABS = [
  "Dashboard",
  "Notifications",
  "Orders",
  "Sales",
  "Custom Orders",
  "Designs",
  "Products",
  "Gifts",
  "Size Charts",
  "Offers",
  "Settings",
];

const ORDER_STATUSES = [
  "new",
  "confirmed",
  "processing",
  "printed",
  "ready",
  "shipped",
  "delivered",
  "cancelled",
];

const DESIGN_GROUPS = [
  "Round Neck T-Shirt",
  "Round Neck - Full Sleeve",
  "Collar T-Shirt",
  "Collar - Full Sleeve",
  "Oversized T-Shirt",
  "Hoodies",
  "Couple T-Shirt",
];

const DESIGN_CATS = [
  "Tamil",
  "Typography",
  "Streetwear",
  "Couple",
  "Sports",
  "College",
  "Corporate",
  "Festival",
  "Minimal",
  "Funny",
  "Motivational",
  "Kids",
  "Custom Name & Number",
];

function csv(v) {
  return (v || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function toCsv(a) {
  return (a || []).map((item) =>
    typeof item === "string" ? item : item?.name || ""
  ).filter(Boolean).join(", ");
}

function sizeChartToText(chart) {
  if (!chart || !Array.isArray(chart.headers) || !Array.isArray(chart.rows)) return "";
  return [chart.headers, ...chart.rows].map((row) => row.map((cell) => String(cell ?? "")).join(" | ")).join("\n");
}

function sizeChartFromText(value) {
  const lines = String(value || "").split(/\r?\n/).map((line) => line.split("|").map((cell) => cell.trim()));
  const nonEmpty = lines.filter((row) => row.some(Boolean));
  if (nonEmpty.length < 2) return { headers: [], rows: [] };
  const headers = nonEmpty[0];
  const rows = nonEmpty.slice(1).filter((row) => row.some(Boolean)).map((row) => headers.map((_, index) => row[index] || ""));
  return { headers, rows };
}

function toQualityPricingCsv(a) {
  return (a || []).map((item) => {
    if (typeof item === "string") return item;
    const name = String(item?.name || "").trim();
    const price = item?.price;
    return name && price !== undefined && price !== null && price !== ""
      ? name + " | " + price
      : name;
  }).filter(Boolean).join(", ");
}

function qualityPricingCsv(value) {
  return csv(value).map((entry) => {
    const parts = entry.split("|");
    const name = (parts.shift() || "").trim();
    const priceText = parts.join("|").trim();
    if (!name) return null;
    if (!priceText) return { name, price: null };
    const price = Number(priceText);
    return Number.isFinite(price) && price > 0
      ? { name, price }
      : null;
  }).filter(Boolean);
}

function absUrl(u) {
  if (!u) return "";
  return u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
}

async function createBlurHash(file) {
  if (!file || !file.type.startsWith("image/")) return "";
  try {
    const bitmap = await createImageBitmap(file);
    const maxSize = 64;
    const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(bitmap, 0, 0, width, height);
    const pixels = ctx.getImageData(0, 0, width, height).data;
    bitmap.close();
    return encode(pixels, width, height, 4, 3);
  } catch {
    return "";
  }
}

function normalizeOrderStatus(value) {
  const status = String(value || "new").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (status === "canceled") return "cancelled";
  if (["ready_for_dispatch", "ready_to_dispatch"].includes(status)) return "ready";
  if (status === "dispatched") return "shipped";
  return status;
}

function formatPaymentStatus(status) {
  if (!status) return "UNKNOWN";

  if (status === "paid") return "PAID";
  if (status === "pending") return "PENDING";

  return String(status).toUpperCase();
}

function formatOrderAmount(order) {
  const amount =
    order.total_amount ??
    order.amount ??
    order.total ??
    order.amount_paid;

  if (
    amount === null ||
    amount === undefined ||
    amount === ""
  ) {
    return "—";
  }

  const numericAmount = Number(amount);

  if (Number.isNaN(numericAmount)) {
    return "—";
  }

  return `₹${numericAmount.toLocaleString("en-IN")}`;
}

export default function AdminDashboard() {
  const nav = useNavigate();

  const [tab, setTab] = useState("Dashboard");

  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [designs, setDesigns] = useState({
    items: [],
    total: 0,
  });
  const [products, setProducts] = useState([]);
  const [customPricing, setCustomPricing] = useState(null);
  const [savingCustomPricing, setSavingCustomPricing] = useState(false);
  const [codEnabled, setCodEnabled] = useState(true);
  const [savingCodEnabled, setSavingCodEnabled] = useState(false);
  const [offers, setOffers] = useState([]);
  const [offerForm, setOfferForm] = useState({id:null,name:"",type:"bundle",active:true,priority:10,eligible_category:"T-Shirts",eligible_product_slugs:"",min_quantity:"0",min_order_value:"0",discount_amount:"0",discount_percent:"0",bundle_quantity:"3",bundle_price:"999",message:"",start_date:"",end_date:""});
  const [savingOffer, setSavingOffer] = useState(false);

  const [notifications, setNotifications] = useState({
    unread: 0,
    items: [],
  });

  const [designPage, setDesignPage] = useState(1);
  const [designSearch, setDesignSearch] = useState("");
  const [designCat, setDesignCat] = useState("All");
  const [designGroup, setDesignGroup] = useState("All");
  const [designStatusFilter, setDesignStatusFilter] = useState("all");
  const [productStatusFilter, setProductStatusFilter] = useState("all");

  const handleDesignSearchChange = (value) => {
    setDesignPage(1);
    setDesignSearch(value);
  };

  const handleDesignCategoryChange = (value) => {
    setDesignPage(1);
    setDesignCat(value);
  };

  const handleDesignGroupChange = (value) => {
    setDesignPage(1);
    setDesignGroup(value);
  };

  const handleDesignStatusChange = (value) => {
    setDesignPage(1);
    setDesignStatusFilter(value);
  };

  const [orderSearch, setOrderSearch] = useState("");

  const [salesDatePreset, setSalesDatePreset] = useState("all");
  const [salesStartDate, setSalesStartDate] = useState("");
  const [salesEndDate, setSalesEndDate] = useState("");
  const [salesSource, setSalesSource] = useState("all");
  const [salesPayment, setSalesPayment] = useState("all");
  const [salesStatus, setSalesStatus] = useState("all");

  const emptyManualItem = () => ({
    product_type: "Custom T-Shirts",
    product_name: "Custom T-Shirts",
    quantity: "1",
    size: "",
    color: "",
    quality: "",
    printing_method: "DTF",
    design_id: "",
    tshirt_style: "",
    custom_name: "",
    custom_number: "",
    custom_text: "",
    gift_options_text: "",
    artwork_url: "",
    unit_price: "",
  });

  const [customOrder, setCustomOrder] = useState({
    name: "",
    phone: "",
    email: "",
    source: "social_media",
    address: "",
    city: "",
    district: "",
    state: "Tamil Nadu",
    pincode: "",
    shipping_charge: "0",
    payment_method: "pending",
    payment_status: "pending",
  });

  const [manualItems, setManualItems] = useState([emptyManualItem()]);

  const [loading, setLoading] = useState(false);

  /* =========================
     DESIGN FORM
  ========================= */

  const [newDesign, setNewDesign] = useState({
    design_id: "",
    name: "",
    group: "Round Neck T-Shirt",
    category: "Tamil",
    custom_category: "",
    description: "",
    image_url: "",
    blurhash: "",
    back_image_url: "",
    images: [],
    price: "",
    discount_percent: "0",
    delivery_charge: "",
    hsn_code: "",
    gst_rate: "",
    colors: "Black, White",
    qualities: "",
    active: true,
    featured: false,
  });

  const [editDesignId, setEditDesignId] =
    useState(null);

  const [editDesign, setEditDesign] =
    useState(null);

  /* =========================
     PRODUCT FORM
  ========================= */

  const [newProduct, setNewProduct] = useState({
    slug: "",
    name: "",
    category: "",
    product_type: "apparel",
    gift_options: [],
    description: "",
    long_description: "",
    sizes: "S, M, L, XL, XXL",
    size_chart_text: "",
    colors: "Black, White, Custom",
    printing_methods: "DTF, Vinyl",
    styles: "",
    qualities: "",
    variant_images: [],
    image: "",
    blurhash: "",
    images: [],
    price: "",
    discount_percent: "0",
    hsn_code: "",
    gst_rate: "",
    purchasable: true,
    active: true,
    featured: false,
  });

  const [editProductSlug, setEditProductSlug] =
    useState(null);

  const [editProduct, setEditProduct] =
    useState(null);

  /* =========================
     AUTH
  ========================= */

  const authCheck = async () => {
    try {
      await api.get("/auth/me");
      // CSRF state is intentionally memory-only, so recover it from the
      // authenticated HttpOnly cookie after a page reload.
      const { data } = await api.get("/auth/csrf");
      if (!data?.csrf_token) throw new Error("CSRF token unavailable");
      setCsrfToken(data.csrf_token);
      return true;
    } catch {
      clearAdminAuth();
      nav("/admin");
      return false;
    }
  };

  /* =========================
     LOAD
  ========================= */

  const loadNotifications = async (showError = false) => {
    try {
      const { data } = await api.get("/notifications");
      const payload = data && typeof data === "object" ? data : {};
      const rawUnread = Number(payload.unread ?? 0);
      setNotifications({
        ...payload,
        unread: Number.isFinite(rawUnread) && rawUnread > 0 ? Math.floor(rawUnread) : 0,
        items: Array.isArray(payload.items) ? payload.items : [],
      });
    } catch (err) {
      if (showError) {
        toast.error(
          err?.response?.data?.detail || "Failed to load notifications"
        );
      }
    }
  };

  const createEkartShipment = async (
    orderNumber, weightGrams, lengthCm, widthCm, heightCm, verifiedNoExistingShipment = false
  ) => {
    try {
      const params = new URLSearchParams({
        verified_no_existing_shipment: String(Boolean(verifiedNoExistingShipment)),
      });
      for (const [key, value] of [
        ["weight_grams", weightGrams],
        ["length_cm", lengthCm],
        ["width_cm", widthCm],
        ["height_cm", heightCm],
      ]) {
        if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
      }
      const { data } = await api.post(
        "/orders/" + encodeURIComponent(orderNumber) + "/ekart-shipment?" + params.toString()
      );
      toast.success(data?.already_created ? "Ekart shipment already exists" : "Ekart shipment created");
      await loadOrders(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create Ekart shipment");
    }
  };

  const loadOrders = async (showError = true) => {
    try {
      const { data } = await api.get("/orders");
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      if (showError) {
        toast.error(
          err?.response?.data?.detail || "Failed to load orders"
        );
      }
    }
  };

  const loadQuotes = async (showError = true) => {
    try {
      const { data } = await api.get("/quotes");
      setQuotes(Array.isArray(data) ? data : []);
    } catch (err) {
      if (showError) {
        toast.error(
          err?.response?.data?.detail || "Failed to load quotes"
        );
      }
    }
  };

  const loadDesigns = async (showError = true) => {
    try {
      const { data } = await api.get("/admin/designs", {
        params: {
          skip: (designPage - 1) * 24,
          limit: 24,
          q: designSearch || undefined,
          category: designCat !== "All" ? designCat : undefined,
          group: designGroup !== "All" ? designGroup : undefined,
          active: designStatusFilter === "all" ? undefined : designStatusFilter === "active",
        },
      });
      setDesigns(
        data && typeof data === "object"
          ? data
          : { items: [], total: 0 }
      );
    } catch (err) {
      if (showError) {
        toast.error(
          err?.response?.data?.detail || "Failed to load designs"
        );
      }
    }
  };

  const loadCustomPricing = async (showError = true) => {
    try {
      const { data } = await api.get("/custom-pricing");
      setCustomPricing(data || null);
    } catch (err) {
      if (showError) toast.error(err?.response?.data?.detail || "Failed to load customization pricing");
    }
  };

  const loadPaymentOptions = async () => {
    try {
      const { data } = await api.get("/payment-options");
      setCodEnabled(data?.cod_enabled !== false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load payment options");
    }
  };

  const saveCodEnabled = async (enabled) => {
    setSavingCodEnabled(true);
    try {
      const { data } = await api.put("/payment-options", { cod_enabled: enabled });
      setCodEnabled(data?.cod_enabled !== false);
      toast.success(enabled ? "Cash on Delivery enabled" : "Cash on Delivery disabled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update Cash on Delivery");
    } finally {
      setSavingCodEnabled(false);
    }
  };

  const saveCustomPricing = async () => {
    if (!customPricing) return;
    setSavingCustomPricing(true);
    try {
      const { data } = await api.put("/custom-pricing", {
        pricing: customPricing.pricing,
        personalisation: customPricing.personalisation,
      });
      setCustomPricing(data);
      toast.success("Customization pricing saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save customization pricing");
    } finally {
      setSavingCustomPricing(false);
    }
  };

  const updateCustomPrice = (group, section, name, value) => {
    setCustomPricing((current) => ({
      ...current,
      pricing: {
        ...current.pricing,
        [group]: {
          ...current.pricing[group],
          [section]: {
            ...current.pricing[group][section],
            [name]: value,
          },
        },
      },
    }));
  };

  const updatePersonalisationPrice = (name, value) => {
    setCustomPricing((current) => ({
      ...current,
      personalisation: {
        ...current.personalisation,
        [name]: value,
      },
    }));
  };

  const loadOffers = async () => {
    try { const {data}=await api.get("/offers/admin"); setOffers(Array.isArray(data)?data:[]); }
    catch(err){ toast.error(err?.response?.data?.detail || "Failed to load offers"); }
  };
  const resetOfferForm=()=>setOfferForm({id:null,name:"",type:"bundle",active:true,priority:10,eligible_category:"T-Shirts",eligible_product_slugs:"",min_quantity:"0",min_order_value:"0",discount_amount:"0",discount_percent:"0",bundle_quantity:"3",bundle_price:"999",message:"",start_date:"",end_date:""});
  const editOffer=(offer)=>{setOfferForm({...offer,eligible_product_slugs:(offer.eligible_product_slugs||[]).join(", ")});setTab("Offers");window.scrollTo({top:0,behavior:"smooth"});};
  const saveOffer=async()=>{if(!offerForm.name.trim())return toast.error("Offer name is required");setSavingOffer(true);try{const payload={...offerForm,priority:Number(offerForm.priority||0),min_quantity:Number(offerForm.min_quantity||0),min_order_value:Number(offerForm.min_order_value||0),discount_amount:Number(offerForm.discount_amount||0),discount_percent:Number(offerForm.discount_percent||0),bundle_quantity:Number(offerForm.bundle_quantity||1),bundle_price:Number(offerForm.bundle_price||0),eligible_product_slugs:String(offerForm.eligible_product_slugs||"").split(",").map(v=>v.trim()).filter(Boolean)};delete payload.id;const r=offerForm.id?await api.put(`/offers/${offerForm.id}`,payload):await api.post("/offers",payload);setOffers(x=>offerForm.id?x.map(o=>o.id===r.data.id?r.data:o):[r.data,...x]);toast.success(offerForm.id?"Offer updated":"Offer created");resetOfferForm();}catch(err){toast.error(err?.response?.data?.detail||"Could not save offer")}finally{setSavingOffer(false)}};
  const toggleOffer=async(offer)=>{try{const p={...offer,active:!offer.active,eligible_product_slugs:offer.eligible_product_slugs||[]};delete p.id;const r=await api.put(`/offers/${offer.id}`,p);setOffers(x=>x.map(o=>o.id===offer.id?r.data:o));}catch(err){toast.error(err?.response?.data?.detail||"Could not update offer")}};
  const deleteOffer=async(offer)=>{if(!window.confirm(`Delete offer "${offer.name}"?`))return;try{await api.delete(`/offers/${offer.id}`);setOffers(x=>x.filter(o=>o.id!==offer.id));if(offerForm.id===offer.id)resetOfferForm();toast.success("Offer deleted")}catch(err){toast.error(err?.response?.data?.detail||"Could not delete offer")}};
  const loadProducts = async (showError = true, statusFilter = productStatusFilter) => {
    try {
      const { data } = await api.get("/admin/products", { params: { active: statusFilter === "all" ? undefined : statusFilter === "active" } });
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      if (showError) {
        toast.error(
          err?.response?.data?.detail || "Failed to load products"
        );
      }
    }
  };


  const saveSizeChart = async (product, chart) => {
    try {
      await api.put(`/products/${encodeURIComponent(product.slug)}`, {
        ...product,
        size_chart: chart,
      });
      setProducts((current) => current.map((item) =>
        item.slug === product.slug ? { ...item, size_chart: chart } : item
      ));
      toast.success(`Size chart saved for ${product.name}`);
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save size chart");
      return false;
    }
  };

  const load = async () => {
    if (!(await authCheck())) return;

    setLoading(true);
    try {
      if (tab === "Dashboard") {
        await Promise.all([
          loadOrders(false),
          loadQuotes(false),
          loadNotifications(false),
        ]);
      } else if (tab === "Notifications") {
        await loadNotifications();
      } else if (tab === "Orders" || tab === "Sales" || tab === "Custom Orders") {
        await loadOrders();
      } else if (tab === "Designs") {
        await loadDesigns();
      } else if (tab === "Products" || tab === "Gifts") {
        await Promise.all([
          loadProducts(),
          loadCustomPricing(false),
        ]);
      } else if (tab === "Size Charts") {
        await loadProducts();
      } else if (tab === "Offers") {
        await loadOffers();
      } else if (tab === "Settings") {
        await Promise.all([loadCustomPricing(), loadPaymentOptions()]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();

    const t = setInterval(() => {
      loadNotifications(false);
    }, 20000);

    return () => clearInterval(t);

    // eslint-disable-next-line
  }, [
    tab,
    designPage,
    designSearch,
    designCat,
    designGroup,
    designStatusFilter,
    productStatusFilter,
  ]);

  useEffect(() => {
    const totalPages = Math.max(
      1,
      Math.ceil((designs.total || 0) / 24)
    );

    if (designPage > totalPages) {
      setDesignPage(totalPages);
    }
  }, [designs.total, designPage]);

  /* =========================
     LOGOUT
  ========================= */

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // The server may already have expired the session.
    } finally {
      clearAdminAuth();
      nav("/admin");
    }
  };

  /* =========================
     UPLOAD
  ========================= */

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    // Let the browser/Axios set the multipart Content-Type including boundary.
    const { data } = await api.post("/upload", fd);
    return absUrl(data.url);
  };

  const uploadWith = async (file, onUploaded) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      return toast.error("Please upload an image file");
    }
    if (file.size > 5 * 1024 * 1024) {
      return toast.error("Image must be under 5 MB");
    }

    const t = toast.loading("Uploading…");
    try {
      const url = await uploadImage(file);
      await onUploaded(url, file);
      toast.success("Uploaded", { id: t });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed", { id: t });
    }
  };

  const handleFileDrop = (onFile) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) onFile(file);
  };

  const handleUploadFile = (file, target, setter) =>
    uploadWith(file, async (url, selectedFile) => {
      const blurhash = await createBlurHash(selectedFile);
      setter((prev) => ({
        ...prev,
        [target]: url,
        ...(target === "image_url" || target === "image" ? { blurhash } : {}),
      }));
    });

  const handleUpload = (target, setter) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleUploadFile(file, target, setter);
    e.target.value = "";
  };

  const handleImageArrayFile = (file, index, setter) =>
    uploadWith(file, async (url) => {
      setter((prev) => {
        const images = Array.isArray(prev.images)
          ? [...prev.images].slice(0, 5)
          : [];
        images[index] = url;
        return { ...prev, images };
      });
    });

  const handleImageArrayUpload = (index, setter) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleImageArrayFile(file, index, setter);
    e.target.value = "";
  };


  const removeImageAt = (index, setter) => {
    setter((prev) => ({
      ...prev,
      images: (Array.isArray(prev.images) ? prev.images : [])
        .filter((_, i) => i !== index)
        .slice(0, 5),
    }));
  };

  /* =========================
     NOTIFICATIONS
  ========================= */

  const markRead = async (id) => {
    try {
      await api.post(
        `/notifications/${id}/read`
      );

      loadNotifications();
    } catch {
      toast.error(
        "Could not mark notification as read"
      );
    }
  };

  const markAllRead = async () => {
    try {
      await api.post(
        "/notifications/read-all"
      );

      loadNotifications();
    } catch {
      toast.error(
        "Could not mark notifications as read"
      );
    }
  };

  /* =========================
     ORDERS
  ========================= */

  const setOrderStatus = async (
    orderNumber,
    status
  ) => {
    try {
      await api.put(
        `/orders/${orderNumber}/status`,
        null,
        {
          params: {
            status,
          },
        }
      );

      // Update the dashboard immediately so the new status is visible
      // without waiting for a full orders reload.
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.order_number === orderNumber
            ? { ...order, status }
            : order
        )
      );

      toast.success(
        `Order → ${status}`
      );

      // Re-fetch in the background to keep the dashboard in sync
      // with the database.
      loadOrders(false);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Could not update order"
      );
    }
  };

  const orderSocialMedia = (order) => {
    const message =
      `Hi ${
        order.full_name ||
        "Customer"
      }, ` +
      `this is PrintMyTees regarding your order ` +
      `${order.order_number}. ` +
      `Order status: ${
        order.status || "new"
      }. ` +
      `Payment status: ${formatPaymentStatus(
        order.payment_status
      )}.`;

    return waLink(message);
  };

  const updateManualItem = (index, field, value) => {
    setManualItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  const addManualItem = () => {
    if (manualItems.length >= 50) {
      toast.error("Maximum 50 products per order");
      return;
    }
    setManualItems((prev) => [...prev, emptyManualItem()]);
  };

  const removeManualItem = (index) => {
    if (manualItems.length === 1) {
      toast.error("Keep at least one product");
      return;
    }
    setManualItems((prev) => prev.filter((_, i) => i !== index));
  };

  const manualSubtotal = manualItems.reduce(
    (sum, item) =>
      sum +
      Number(item.unit_price || 0) * Number(item.quantity || 0),
    0
  );

  const manualGrandTotal =
    manualSubtotal + Number(customOrder.shipping_charge || 0);

  const addCustomOrder = async (e) => {
    e.preventDefault();

    if (
      !customOrder.name.trim() ||
      !customOrder.phone.trim() ||
      !customOrder.address.trim() ||
      !customOrder.city.trim() ||
      !customOrder.district.trim() ||
      !customOrder.pincode.trim()
    ) {
      toast.error("Customer name, phone and complete delivery address are required");
      return;
    }

    if (
      manualItems.some(
        (item) =>
          !item.product_name.trim() ||
          !item.quantity ||
          !item.unit_price
      )
    ) {
      toast.error("Enter product name, quantity and price for every product");
      return;
    }

    try {
      const { data } = await api.post("/admin/orders/manual", {
        full_name: customOrder.name,
        mobile: customOrder.phone,
        email: customOrder.email || null,
        source: customOrder.source,
        address: customOrder.address,
        city: customOrder.city,
        district: customOrder.district,
        state: customOrder.state,
        pincode: customOrder.pincode,
        shipping_charge: Number(customOrder.shipping_charge || 0),
        payment_method: customOrder.payment_method,
        payment_status: customOrder.payment_status,
        items: manualItems.map((item) => {
          const giftOptions = {};
          const giftOptionLabels = {};
          const giftLines = String(item.gift_options_text || "")
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean);
          if (giftLines.length > 20) {
            throw new Error("Each manual product can have at most 20 gift personalisation fields");
          }
          giftLines.forEach((line, index) => {
            const separator = line.indexOf(":");
            if (separator <= 0 || !line.slice(separator + 1).trim()) {
              throw new Error("Gift personalisation must use one 'Label: value' entry per line");
            }
            const label = line.slice(0, separator).trim();
            const value = line.slice(separator + 1).trim();
            const key = `gift_field_${index + 1}`;
            giftOptions[key] = value;
            giftOptionLabels[key] = label;
          });
          const { gift_options_text, ...itemPayload } = item;
          return {
            ...itemPayload,
            quantity: Number(item.quantity || 1),
            unit_price: Number(item.unit_price),
            gift_options: giftOptions,
            gift_option_labels: giftOptionLabels,
          };
        }),
      });

      toast.success(
        `Order ${data.order_number} with ${data.items?.length || manualItems.length} products added successfully`
      );

      setCustomOrder({
        name: "",
        phone: "",
        email: "",
        source: "social_media",
        address: "",
        city: "",
        district: "",
        state: "Tamil Nadu",
        pincode: "",
        shipping_charge: "0",
        payment_method: "pending",
        payment_status: "pending",
      });
      setManualItems([emptyManualItem()]);
      loadOrders();
      loadNotifications();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message || "Could not create manual order"
      );
    }
  };

  const customOrderWhatsApp = (q) => {
    const message = [
      `Hi ${q.name}, this is PrintMyTees regarding your custom order.`,
      `Product: ${q.product_type}`,
      `Quantity: ${q.quantity}`,
      q.size ? `Size(s): ${q.size}` : "",
      q.color ? `Colour: ${q.color}` : "",
      q.printing_method ? `Printing: ${q.printing_method}` : "",
      q.design_id ? `Design: ${q.design_id}` : "",
      q.instructions ? `Instructions: ${q.instructions}` : "",
    ].filter(Boolean).join("\n");
    return waLink(message);
  };

  const getSalesDateRange = () => {
    const today = new Date();
    const end = new Date(today);
    end.setHours(23, 59, 59, 999);
    const startDate = new Date(today);
    startDate.setHours(0, 0, 0, 0);

    if (salesDatePreset === "today") {
      return { start: startDate, end: end };
    }

    if (salesDatePreset === "week") {
      const day = startDate.getDay();
      const mondayOffset = day === 0 ? 6 : day - 1;
      startDate.setDate(startDate.getDate() - mondayOffset);
      return { start: startDate, end: end };
    }

    if (salesDatePreset === "month") {
      startDate.setDate(1);
      return { start: startDate, end: end };
    }

    if (salesDatePreset === "year") {
      startDate.setMonth(0, 1);
      return { start: startDate, end: end };
    }

    if (salesDatePreset === "custom") {
      const customStart = salesStartDate
        ? new Date(`${salesStartDate}T00:00:00`)
        : null;
      const customEnd = salesEndDate
        ? new Date(`${salesEndDate}T23:59:59`)
        : null;
      return { start: customStart, end: customEnd };
    }

    return { start: null, end: null };
  };

  const getOrderAmount = (order) => {
    const amount = Number(order?.total_amount ?? 0);
    return Number.isFinite(amount) ? amount : 0;
  };

  const isManualOrder = (order) => {
    const source = String(order?.source || order?.order_source || "online")
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");
    // Unknown source labels must not turn an unpaid online attempt into a sale.
    // Keep explicit manual channels and the legacy manual-order marker.
    const manualSources = new Set([
      "social_media", "phone", "walk_in", "whatsapp", "manual",
      "manual_order", "instagram", "facebook",
    ]);
    return manualSources.has(source) ||
      order?.product_slug === "manual-custom-order" ||
      (Array.isArray(order?.items) ? order.items : []).some((item) => item?.product_slug === "manual-custom-order");
  };

  const salesOrders = orders.filter((order) => normalizeOrderStatus(order.status) !== "cancelled");

  const { start: salesRangeStart, end: salesRangeEnd } = getSalesDateRange();

  const normalizeOrderSource = (value) => {
    const source = String(value || "online")
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");

    // Legacy checkout orders may use different names for the same online source.
    // Keep them grouped under "online" so filters and source breakdowns agree.
    if (["", "online", "website", "web", "checkout", "prepaid", "pre_paid"].includes(source)) {
      return "online";
    }
    return source === "walk_in" ? "walk-in" : source;
  };

  const filteredSalesOrders = salesOrders.filter((order) => {
    const source = normalizeOrderSource(order.source || order.order_source);
    const selectedSource = normalizeOrderSource(salesSource);
    const payment = String(order.payment_status || "pending").trim().toLowerCase();
    const status = normalizeOrderStatus(order.status);
    const isManual = isManualOrder(order);
    const paymentMethod = String(order.payment_method || "")
      .trim()
      .toLowerCase()
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ");
    const isCod = ["cod", "cash on delivery"].includes(paymentMethod);

    // Pending online checkout attempts are false orders, not sales. Keep
    // manual orders and COD orders visible while their payment is outstanding.
    if (!isManual && !isCod && payment !== "paid") return false;

    if (salesSource !== "all" && source !== selectedSource) return false;
    if (salesPayment !== "all" && payment !== salesPayment) return false;
    if (salesStatus !== "all" && status !== salesStatus) return false;

    if (salesRangeStart || salesRangeEnd) {
      const created = order.created_at ? new Date(order.created_at) : null;
      if (!created || Number.isNaN(created.getTime())) return false;
      if (salesRangeStart && created < salesRangeStart) return false;
      if (salesRangeEnd && created > salesRangeEnd) return false;
    }

    return true;
  });

  const salesTotal = filteredSalesOrders.reduce(
    (sum, order) => sum + getOrderAmount(order),
    0
  );

  const salesPaid = filteredSalesOrders
    .filter((order) => String(order.payment_status || "").trim().toLowerCase() === "paid")
    .reduce((sum, order) => sum + getOrderAmount(order), 0);

  const salesPending = filteredSalesOrders
    .filter((order) => String(order.payment_status || "pending").trim().toLowerCase() !== "paid")
    .reduce((sum, order) => sum + getOrderAmount(order), 0);

  const salesOrderCount = filteredSalesOrders.length;

  const salesItemCount = filteredSalesOrders.reduce(
    (sum, order) =>
      sum +
      (Array.isArray(order.items) ? order.items : []).reduce((itemSum, item) => {
        const quantity = Number(item?.quantity ?? 0);
        return itemSum + (Number.isFinite(quantity) ? Math.max(0, quantity) : 0);
      }, 0),
    0
  );

  const manualSales = filteredSalesOrders
    .filter(isManualOrder)
    .reduce((sum, order) => sum + getOrderAmount(order), 0);

  const onlineSales = filteredSalesOrders
    .filter((order) => !isManualOrder(order))
    .reduce((sum, order) => sum + getOrderAmount(order), 0);

  const cancelledSales = orders
    .filter((order) => normalizeOrderStatus(order.status) === "cancelled")
    .reduce((sum, order) => sum + getOrderAmount(order), 0);

  const normalizePaymentMethod = (value) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[-_ ]+/g, " ");

    if (normalized === "upi") return "upi";
    if (normalized === "card" || normalized === "credit/debit card" || normalized === "credit debit card") return "card";
    if (normalized === "net banking" || normalized === "netbanking") return "net_banking";
    if (normalized === "wallet") return "wallet";
    if (normalized === "cash") return "cash";
    if (normalized === "bank transfer") return "bank_transfer";
    if (normalized === "cod" || normalized === "cash on delivery") return "cod";
    return "";
  };

  const paymentBreakdown = ["upi", "card", "net_banking", "wallet", "cash", "bank_transfer", "cod", "partial", "pending"].map((method) => ({
    method,
    amount: filteredSalesOrders
      .filter((order) => {
        const paymentStatus = String(order.payment_status || "pending").trim().toLowerCase();
        if (method === "partial") return paymentStatus === "partial";
        if (method === "pending") return paymentStatus !== "paid" && paymentStatus !== "partial";
        return paymentStatus === "paid" && normalizePaymentMethod(order.payment_method) === method;
      })
      .reduce((sum, order) => sum + getOrderAmount(order), 0),
  }));

  const sourceBreakdown = ["online", "social_media", "phone", "walk-in"].map((source) => ({
    source,
    amount: filteredSalesOrders
      .filter((order) => normalizeOrderSource(order.source || order.order_source) === normalizeOrderSource(source))
      .reduce((sum, order) => sum + getOrderAmount(order), 0),
  }));

  const productSalesMap = {};
  filteredSalesOrders.forEach((order) => {
    (Array.isArray(order.items) ? order.items : []).forEach((item) => {
      const name = item?.product_name || "Unnamed Product";
      if (!productSalesMap[name]) {
        productSalesMap[name] = { name, quantity: 0, amount: 0 };
      }
      const rawQuantity = Number(item?.quantity ?? 0);
      const rawUnitPrice = Number(item?.unit_price ?? 0);
      const quantity = Number.isFinite(rawQuantity) ? Math.max(0, rawQuantity) : 0;
      const unitPrice = Number.isFinite(rawUnitPrice) ? Math.max(0, rawUnitPrice) : 0;
      productSalesMap[name].quantity += quantity;
      productSalesMap[name].amount += quantity * unitPrice;
    });
  });

  const productSales = Object.values(productSalesMap)
    .sort((a, b) => b.amount - a.amount)
    .map((item) => ({
      ...item,
      percentage: salesTotal > 0 ? (item.amount / salesTotal) * 100 : 0,
    }));

  const filteredOrders =
    orders.filter((order) => {
      const paymentMethod = String(order.payment_method || "")
        .trim()
        .toLowerCase()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ");
      const isCod = ["cod", "cash on delivery"].includes(paymentMethod);
      const isPaid = String(order.payment_status || "").trim().toLowerCase() === "paid";
      const isManual = isManualOrder(order);

      // Hide only unpaid online checkout attempts. Manually entered orders
      // remain visible for sales tracking, and COD remains visible until collected.
      if (!isManual && !isCod && !isPaid) return false;

      if (!orderSearch.trim()) return true;

      const q = orderSearch.trim().toLowerCase();
      const searchableOrderValues = [
        order.order_number,
        order.full_name,
        order.mobile,
        order.phone,
        order.email,
        order.status,
        order.payment_status,
        order.payment_method,
        order.address,
        order.address_line,
        order.address_line_2,
        order.landmark,
        order.city,
        order.district,
        order.state,
        order.pincode,
        order.ekart_tracking_id,
        order.ekart_waybill,
        ...(Array.isArray(order.items) ? order.items.flatMap((item) => [
          item.product_name,
          item.product_slug,
          item.design_id,
          item.size,
          item.color,
          item.printing_method,
          item.custom_name,
          item.custom_number,
          item.custom_text,
          ...(item.gift_options && typeof item.gift_options === "object" ? Object.values(item.gift_options) : []),
        ]) : []),
      ];
      return searchableOrderValues
        .filter((value) => value !== null && value !== undefined && String(value).trim() !== "")
        .some((value) => String(value).toLowerCase().includes(q));
    });

  // Dashboard activity should not be affected by a search performed in the Orders tab.
  const dashboardRecentOrders = orders
    .filter((order) => {
      if (normalizeOrderStatus(order.status) === "cancelled") return false;
      const paymentMethod = String(order.payment_method || "")
        .trim()
        .toLowerCase()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ");
      const isCod = ["cod", "cash on delivery"].includes(paymentMethod);
      const isPaid = String(order.payment_status || "").trim().toLowerCase() === "paid";
      return isManualOrder(order) || isCod || isPaid;
    })
    .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
    .slice(0, 5);

  /* =========================
     DESIGNS
  ========================= */

  const addDesign = async (e) => {
    e.preventDefault();

    try {
      await api.post(
        "/designs",
        {
          design_id:
            newDesign.design_id,

          name:
            newDesign.name,

          group:
            newDesign.group,

          category:
            newDesign.category === "Custom"
              ? (newDesign.custom_category || "").trim()
              : newDesign.category,

          description:
            newDesign.description,

          image_url:
            newDesign.image_url,

          blurhash:
            newDesign.blurhash || "",

          back_image_url:
            newDesign.back_image_url || "",

          images:
            (newDesign.images || []).filter(Boolean).slice(0, 5),

          price:
            newDesign.price === ""
              ? null
              : Number(
                  newDesign.price
                ),

          discount_percent:
            newDesign.discount_percent === "" ||
            newDesign.discount_percent == null
              ? 0
              : Number(newDesign.discount_percent),

          delivery_charge:
            newDesign.delivery_charge === ""
              ? 0
              : Number(newDesign.delivery_charge),

          hsn_code:
            (newDesign.hsn_code || "").trim(),

          gst_rate:
            newDesign.gst_rate === "" ||
            newDesign.gst_rate == null
              ? null
              : Number(newDesign.gst_rate),

          colors:
            csv(
              newDesign.colors
            ),
          qualities:
            qualityPricingCsv(newDesign.qualities),

          tags: [
            (newDesign.category === "Custom"
              ? (newDesign.custom_category || "").trim()
              : newDesign.category
            ).toLowerCase(),
          ],

          palette: [],

          active:
            newDesign.active,

          featured:
            newDesign.featured === true,
        }
      );

      toast.success(
        "Design added"
      );

      setNewDesign({
        design_id: "",
        name: "",
        group:
          "Round Neck T-Shirt",
        category: "Tamil",
        custom_category: "",
        description: "",
        image_url: "",
        blurhash: "",
        back_image_url: "" ,
        images: [],
        price: "",
        discount_percent: "0",
        delivery_charge: "",
        hsn_code: "",
        gst_rate: "",
        colors:
          "Black, White",
        qualities:
          "",
        active: true,
        featured: false,
      });

      load();
    } catch (err) {
      toast.error(
        err?.response?.data
          ?.detail ||
          "Failed"
      );
    }
  };

  const startEditDesign = (
    d
  ) => {
    setEditDesignId(
      d.design_id
    );

    setEditDesign({
      design_id:
        d.design_id,

      name:
        d.name,

      group:
        d.group ||
        "Round Neck T-Shirt",

      category:
        DESIGN_CATS.includes(d.category) ? d.category : "Custom",

      custom_category:
        DESIGN_CATS.includes(d.category) ? "" : (d.category || ""),

      description:
        d.description || "",

      image_url:
        d.image_url || "",

      blurhash:
        d.blurhash || "",

      back_image_url:
        d.back_image_url || "",

      images:
        Array.isArray(d.images) ? d.images.slice(0, 5) : [],

      price:
        d.price ?? "",

      discount_percent:
        d.discount_percent ?? 0,

      delivery_charge:
        d.delivery_charge ?? "",

      hsn_code:
        d.hsn_code || "",

      gst_rate:
        d.gst_rate ?? "",

      colors:
        toCsv(d.colors),
      qualities:
        toQualityPricingCsv(d.qualities),

      active:
        d.active !== false,

      featured:
        d.featured === true,

      tags:
        d.tags || [],

      palette:
        d.palette || [],
    });
  };

  const saveEditDesign =
    async () => {
      try {
        await api.put(
          `/designs/${editDesignId}`,
          {
            design_id:
              editDesign.design_id,

            name:
              editDesign.name,

            group:
              editDesign.group ||
              "Round Neck T-Shirt",

            category:
              editDesign.category === "Custom"
                ? (editDesign.custom_category || "").trim()
                : editDesign.category,

            description:
              editDesign.description ||
              "",

            image_url:
              editDesign.image_url ||
              "",

            blurhash:
              editDesign.blurhash ||
              "",

            back_image_url:
              editDesign.back_image_url ||
              "",

            images:
              (editDesign.images || []).filter(Boolean).slice(0, 5),

            price:
              editDesign.price ===
                "" ||
              editDesign.price ==
                null
                ? null
                : Number(
                    editDesign.price
                  ),

            discount_percent:
              editDesign.discount_percent === "" ||
              editDesign.discount_percent == null
                ? 0
                : Number(editDesign.discount_percent),

            delivery_charge:
              editDesign.delivery_charge ===
                "" ||
              editDesign.delivery_charge ==
                null
                ? 0
                : Number(
                    editDesign.delivery_charge
                  ),

            hsn_code:
              (editDesign.hsn_code || "").trim(),

            gst_rate:
              editDesign.gst_rate === "" ||
              editDesign.gst_rate == null
                ? null
                : Number(editDesign.gst_rate),

            colors:
              csv(
                editDesign.colors
              ),
            qualities:
              qualityPricingCsv(editDesign.qualities),

            tags:
              editDesign.tags ||
              [],

            palette:
              editDesign.palette ||
              [],

            active:
              editDesign.active !==
              false,

            featured:
              editDesign.featured === true,
          }
        );

        toast.success(
          "Updated"
        );

        setEditDesignId(
          null
        );

        setEditDesign(
          null
        );

        load();
      } catch (err) {
        toast.error(
          err?.response?.data
            ?.detail ||
            "Failed"
        );
      }
    };

  const delDesign =
    async (id) => {
      if (
        !window.confirm(
          `Delete ${id}?`
        )
      ) {
        return;
      }

      try {
        await api.delete(
          `/designs/${id}`
        );

        toast.success(
          "Deleted"
        );

        load();
      } catch (err) {
        toast.error(
          err?.response?.data
            ?.detail ||
            "Failed to delete design"
        );
      }
    };

  const toggleDesign =
    async (d) => {
      try {
        await api.patch(
          `/designs/${encodeURIComponent(d.design_id)}/active`,
          { active: !d.active }
        );

        load();
      } catch (err) {
        toast.error(
          err?.response?.data
            ?.detail ||
            "Failed"
        );
      }
    };

  /* =========================
     PRODUCTS
  ========================= */

  const giftOptionsError = (product) => {
    if (product?.product_type !== "gift") return "";
    const options = Array.isArray(product.gift_options) ? product.gift_options : [];
    if (options.length > 20) return "Gift products can have at most 20 personalisation fields.";
    const names = new Set();
    for (const option of options) {
      const name = typeof option?.name === "string" ? option.name : "";
      const label = typeof option?.label === "string" ? option.label.trim() : "";
      if (!name || name !== name.trim() || !/^[a-zA-Z0-9_]+$/.test(name) || !label) {
        return "Each gift field needs a label and a valid key using letters, numbers or underscores.";
      }
      const normalizedName = name.toLocaleLowerCase();
      if (names.has(normalizedName)) return "Gift field keys must be unique. Check the [key: ...] directives.";
      names.add(normalizedName);
      if (!["text", "textarea", "select"].includes(option.type || "text")) {
        return "Gift fields can only use text, textarea or select types.";
      }
      if (option.type === "select") {
        const choices = Array.isArray(option.choices) ? option.choices : [];
        if (!choices.length || choices.length > 30 || choices.some((choice) => typeof choice !== "string" || !choice.trim())) {
          return "Each dropdown needs between 1 and 30 non-empty choices.";
        }
        const normalizedChoices = choices.map((choice) => choice.trim().toLocaleLowerCase());
        if (new Set(normalizedChoices).size !== normalizedChoices.length) return "Dropdown choices must be unique.";
      }
    }
    return "";
  };

  const addProduct =
    async (e) => {
      e.preventDefault();
      const giftError = giftOptionsError(newProduct);
      if (giftError) {
        toast.error(giftError);
        return;
      }

      try {
        const payload = {
          ...newProduct,

          sizes:
            csv(
              newProduct.sizes
            ),

          colors:
            csv(
              newProduct.colors
            ),

          printing_methods:
            csv(
              newProduct.printing_methods
            ),

          styles: csv(newProduct.styles),
          qualities: qualityPricingCsv(newProduct.qualities),
          size_chart: sizeChartFromText(newProduct.size_chart_text),
          variant_images: (newProduct.variant_images || []).filter((v) => v && v.image).slice(0, 200),

          price:
            newProduct.price ===
            ""
              ? null
              : Number(
                  newProduct.price
                ),

          discount_percent:
            newProduct.discount_percent === "" ||
            newProduct.discount_percent == null
              ? 0
              : Number(newProduct.discount_percent),

          gst_rate:
            newProduct.gst_rate === "" ||
            newProduct.gst_rate == null
              ? null
              : Number(newProduct.gst_rate),

          images: (newProduct.images || []).filter(Boolean).slice(0, 5),
          image:
            newProduct.image ||
            (newProduct.images || []).filter(Boolean)[0] ||
            "",

          blurhash:
            newProduct.blurhash || "",
        };

        await api.post(
          "/products",
          payload
        );

        toast.success(
          "Product added"
        );

        setNewProduct({
          slug: "",
          name: "",
          category: "",
          product_type: tab === "Gifts" ? "gift" : "apparel",
          featured: newProduct.featured === true,
          gift_options: [],
          description: "",
          long_description: "",
          sizes:
            "S, M, L, XL, XXL",
          size_chart_text: "",
          colors:
            "Black, White, Custom",
          printing_methods:
            "DTF, Vinyl",
          styles:
            "",
          qualities:
            "",
          variant_images:
            [],
          discount_percent:
            "0",
          image: "",
          blurhash: "",
          images: [],
          price: "",
          hsn_code: "",
          gst_rate: "",
          purchasable: true,
          active: true,
          featured: false,
        });

        load();
      } catch (err) {
        toast.error(
          err?.response?.data
            ?.detail ||
            "Failed"
        );
      }
    };

  const startEditProduct =
    (p) => {
      setEditProductSlug(
        p.slug
      );

      setEditProduct({
        slug: p.slug,
        name: p.name,
        category: p.category,
        product_type: p.product_type || "apparel",
        gift_options: p.gift_options || [],
        description:
          p.description,
        long_description:
          p.long_description ||
          "",
        sizes:
          toCsv(p.sizes),
        size_chart_text: sizeChartToText(p.size_chart),
        qualities:
          toQualityPricingCsv(p.qualities),
        styles:
          toCsv(p.styles),
        variant_images:
          p.variant_images || [],
        colors:
          toCsv(p.colors),
        printing_methods:
          toCsv(
            p.printing_methods
          ),
        image:
          p.image || "",
        blurhash:
          p.blurhash || "",
        images:
          p.images || [],
        price:
          p.price ?? "",
        discount_percent:
          p.discount_percent ?? 0,
        hsn_code:
          p.hsn_code || "",
        gst_rate:
          p.gst_rate ?? "",
        purchasable:
          p.purchasable !==
          false,
        active:
          p.active !== false,
        featured: p.featured === true,
        price_note:
          p.price_note ||
          "Get Quote",
      });
    };

  const handleVariantImageFile = (file, style, color, setter) =>
    uploadWith(file, async (url) => {
      setter((prev) => {
        const variants = Array.isArray(prev.variant_images) ? [...prev.variant_images] : [];
        const index = variants.findIndex(
          (v) => (v.style || "") === style && (v.color || "") === color
        );
        const item = { style, color, image: url };
        if (index >= 0) variants[index] = item;
        else variants.push(item);
        return { ...prev, variant_images: variants };
      });
    });

  const handleVariantImageUpload = (style, color, setter) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleVariantImageFile(file, style, color, setter);
    e.target.value = "";
  };

  const handleVariantImageDrop = (style, color, setter) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleVariantImageFile(file, style, color, setter);
  };  const removeVariantImage = (style, color, setter) => {
    setter((prev) => ({
      ...prev,
      variant_images: (prev.variant_images || []).filter((v) => (v.style || "") !== style || (v.color || "") !== color),
    }));
  };

  const variantImageFor = (variants, style, color) =>
    (variants || []).find((v) => (v.style || "") === style && (v.color || "") === color)?.image || "";

  const saveEditProduct =
    async () => {
      const giftError = giftOptionsError(editProduct);
      if (giftError) {
        toast.error(giftError);
        return;
      }

      try {
        const payload = {
          ...editProduct,
          product_type: tab === "Gifts" ? "gift" : (editProduct.product_type === "gift" ? "apparel" : (editProduct.product_type || "apparel")),
          featured: editProduct.featured === true,
          images:
            (editProduct.images || []).filter(Boolean).slice(0, 5),
          image:
            editProduct.image ||
            (editProduct.images || []).filter(Boolean)[0] ||
            "",

          blurhash:
            editProduct.blurhash || "",

          sizes:
            csv(
              editProduct.sizes
            ),

          colors:
            csv(
              editProduct.colors
            ),

          printing_methods:
            csv(
              editProduct.printing_methods
            ),
          styles: csv(editProduct.styles),
          qualities: qualityPricingCsv(editProduct.qualities),
          size_chart: sizeChartFromText(editProduct.size_chart_text),
          variant_images: (editProduct.variant_images || []).filter((v) => v && v.image).slice(0, 200),

          gst_rate:
            editProduct.gst_rate === "" ||
            editProduct.gst_rate == null
              ? null
              : Number(editProduct.gst_rate),

          price:
            editProduct.price ===
              "" ||
            editProduct.price ==
              null
              ? null
              : Number(
                  editProduct.price
                ),

          discount_percent:
            editProduct.discount_percent === "" ||
            editProduct.discount_percent == null
              ? 0
              : Number(editProduct.discount_percent),
        };

        await api.put(
          `/products/${encodeURIComponent(
            editProductSlug
          )}`,
          payload
        );

        toast.success(
          "Updated"
        );

        setEditProductSlug(
          null
        );

        setEditProduct(
          null
        );

        load();
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const message = Array.isArray(detail)
          ? detail
              .map((item) => {
                const field = Array.isArray(item?.loc)
                  ? item.loc.filter((part) => part !== "body").join(".")
                  : "";
                return field
                  ? `${field}: ${item?.msg || "Invalid value"}`
                  : item?.msg || "Validation error";
              })
              .join(" | ")
          : typeof detail === "string"
            ? detail
            : "Failed";
        toast.error(message);
      }
    };

  const delProduct =
    async (slug) => {
      if (
        !window.confirm(
          `Delete product ${slug}?`
        )
      ) {
        return;
      }

      try {
        await api.delete(
          `/products/${encodeURIComponent(
            slug
          )}`
        );

        toast.success(
          "Deleted"
        );

        load();
      } catch (err) {
        toast.error(
          err?.response?.data
            ?.detail ||
            "Failed to delete product"
        );
      }
    };

  const toggleProduct =
    async (p) => {
      try {
        await api.put(
          `/products/${encodeURIComponent(
            p.slug
          )}`,
          {
            ...p,
            active:
              !p.active,
            sizes:
              p.sizes || [],
            colors:
              p.colors || [],
            printing_methods:
              p.printing_methods ||
              [],
            images:
              p.images || [],
          }
        );

        load();
      } catch (err) {
        toast.error(
          err?.response?.data
            ?.detail ||
            "Failed"
        );
      }
    };

  /* =========================
     STATS
  ========================= */

  const manualOrders = orders.filter(isManualOrder);

  // Keep cancelled orders visible in the order count, but exclude them from
  // the sales total, matching the Sales Details report.
  const salesEligibleOrders = orders.filter(
    (order) => normalizeOrderStatus(order.status) !== "cancelled"
  );
  const totalSales = salesEligibleOrders.reduce(
    (sum, order) => sum + getOrderAmount(order),
    0
  );

  const isPaidOrder = (order) =>
    String(order.payment_status || "").trim().toLowerCase() === "paid";

  // Match the Sales report's all-time eligibility rules: exclude cancelled
  // orders and unpaid online checkout attempts, but retain manual and COD orders.
  const dashboardSalesOrders = orders.filter((order) => {
    if (normalizeOrderStatus(order.status) === "cancelled") return false;
    const payment = String(order.payment_status || "pending").trim().toLowerCase();
    const method = String(order.payment_method || "")
      .trim()
      .toLowerCase()
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ");
    const isCod = ["cod", "cash on delivery"].includes(method);
    return isManualOrder(order) || isCod || payment === "paid";
  });
  const dashboardTotalSales = dashboardSalesOrders.reduce(
    (sum, order) => sum + getOrderAmount(order),
    0
  );
  const collectedRevenue = dashboardSalesOrders
    .filter((order) => String(order.payment_status || "").trim().toLowerCase() === "paid")
    .reduce((sum, order) => sum + getOrderAmount(order), 0);
  const readyToDispatch = dashboardSalesOrders.filter(
    (order) => normalizeOrderStatus(order.status) === "ready"
  ).length;

  const stats = {
    orders: orders.length,
    new: dashboardSalesOrders.filter((order) => normalizeOrderStatus(order.status) === "new").length,
    paid: dashboardSalesOrders.filter(isPaidOrder).length,
    pendingPayment: dashboardSalesOrders.filter((order) => !isPaidOrder(order)).length,
    manualOrders: manualOrders.length,
    totalSales: dashboardTotalSales,
    quotes: quotes.length,
    designs: designs.total,
    products: products.length,
    collectedRevenue,
    readyToDispatch,
  };

  const renderDashboardPricingPreview = (category) => {
    const normalizedCategory = String(category || "").trim().toLowerCase();
    const isCustomTShirt =
      normalizedCategory === "custom-t-shirts" ||
      normalizedCategory === "custom t-shirts" ||
      normalizedCategory === "t-shirts" ||
      normalizedCategory === "tshirt";

    if (!isCustomTShirt || !customPricing?.pricing?.["T-Shirts"]) {
      return null;
    }

    const config = customPricing.pricing["T-Shirts"];

    return (
      <div className="md:col-span-3 border border-[#E6DFD5] bg-[#F8F5F0] p-4">
        <div className="overline">Dashboard Customization Pricing</div>
        <div className="mt-1 text-sm font-semibold">T-Shirts</div>
        <div className="text-[10px] text-stone-500 mt-1">
          Read-only here. Change these values from Settings → Customization Pricing.
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="label-pmt">Base / Style Prices</div>
            <div className="mt-2 space-y-2">
              {Object.entries(config.styles || {}).map(([name, value]) => (
                <div key={name} className="flex items-center justify-between border border-[#E6DFD5] bg-white px-3 py-2 text-xs">
                  <span>{name}</span>
                  <span className="font-semibold">₹{Number(value || 0).toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="label-pmt">Printing Charges</div>
            <div className="mt-2 space-y-2">
              {Object.entries(config.printing || {}).map(([name, value]) => (
                <div key={name} className="flex items-center justify-between border border-[#E6DFD5] bg-white px-3 py-2 text-xs">
                  <span>{name}</span>
                  <span className="font-semibold">₹{Number(value || 0).toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2">
            <div className="label-pmt">Personalisation</div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
              {Object.entries(customPricing.personalisation || {}).map(([name, value]) => (
                <div key={name} className="flex items-center justify-between border border-[#E6DFD5] bg-white px-3 py-2 text-xs">
                  <span>
                    {name === "name"
                      ? "Custom Name"
                      : name === "number"
                      ? "Jersey Number"
                      : "Custom Text"}
                  </span>
                  <span className="font-semibold">₹{Number(value || 0).toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* =========================
     UI
  ========================= */

  return (
    <div
      className="container-pmt py-10"
      data-testid="admin-dashboard"
    >
      {/* HEADER */}

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="overline">
            Admin
          </div>

          <h1 className="display-h2 mt-2">
            Dashboard
          </h1>
        </div>

        <div className="flex gap-2">
          <button
            onClick={load}
            className="btn-outline-pmt"
            disabled={loading}
            data-testid="admin-refresh"
          >
            <RefreshCw
              size={14}
              className={
                loading
                  ? "animate-spin"
                  : ""
              }
            />

            Refresh
          </button>

          <button
            onClick={logout}
            className="btn-outline-pmt"
            data-testid="admin-logout"
          >
            Logout
          </button>
        </div>
      </div>

      {/* TABS */}

      <div className="mt-6 flex flex-wrap gap-2 border-y border-[#E6DFD5] py-3 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => {
              setTab(t);
              if (t === "Gifts") setNewProduct((current) => ({ ...current, product_type: "gift" }));
              if (t === "Products") setNewProduct((current) => ({ ...current, product_type: "apparel" }));
            }}
            data-testid={`admin-tab-${t.toLowerCase()}`}
            className={`relative px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border whitespace-nowrap ${
              tab === t
                ? "bg-[#141413] text-[#FAF7F2] border-[#141413]"
                : "bg-white border-[#E6DFD5]"
            }`}
          >
            {t ===
            "Notifications" ? (
              <span className="inline-flex items-center gap-1">
                <Bell size={12} />
                {t}
              </span>
            ) : (
              t
            )}

            {t ===
              "Notifications" &&
              notifications.unread >
                0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-[#D95328] text-white">
                  {
                    notifications.unread
                  }
                </span>
              )}
          </button>
        ))}
      </div>

      {/* DASHBOARD */}
      {tab === "Dashboard" && (
        <div className="mt-6 space-y-6" data-testid="admin-overview">
          <section className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4" data-testid="admin-stats">
            {[
              { label: "All Orders", value: stats.orders, hint: "All recorded orders", icon: ClipboardList },
              { label: "Sales Value", value: `₹${stats.totalSales.toLocaleString("en-IN")}`, hint: "Excludes cancelled and unpaid online attempts", icon: IndianRupee },
              { label: "Collected Revenue", value: `₹${stats.collectedRevenue.toLocaleString("en-IN")}`, hint: "Orders marked paid", icon: IndianRupee },
              { label: "New Orders", value: stats.new, hint: "Need review", icon: Package },
              { label: "Pending Payment", value: stats.pendingPayment, hint: "Check payment status", icon: Clock3 },
              { label: "Ready to Dispatch", value: stats.readyToDispatch, hint: "Ready for courier handover", icon: Truck },
              { label: "Custom Enquiries", value: stats.quotes, hint: "Submitted quote requests", icon: MessageCircle },
              { label: "Unread Alerts", value: notifications.unread, hint: "Notifications to review", icon: Bell },
            ].map(({ label, value, hint, icon: Icon }) => (
              <div key={label} className="card-pmt min-w-0 p-4 sm:p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="overline">{label}</div>
                  <Icon size={17} className="shrink-0 text-[#D95328]" aria-hidden="true" />
                </div>
                <div className="mt-3 break-words text-2xl font-black sm:text-3xl" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                  {value}
                </div>
                <p className="mt-2 text-xs leading-relaxed text-stone-500">{hint}</p>
              </div>
            ))}
          </section>

          <AdminDashboardActionCenter orders={orders} quotes={quotes} unread={notifications.unread} onNavigate={setTab} />

          <AdminDashboardSalesPerformance orders={orders} />

          <section className="card-pmt p-4 sm:p-5" aria-labelledby="admin-quick-actions-title">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="overline">Shortcuts</div>
                <h2 id="admin-quick-actions-title" className="mt-1 text-xl font-bold">Quick actions</h2>
                <p className="mt-1 text-sm text-stone-600">Jump straight to the tasks you use most.</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
              <button type="button" onClick={() => { setTab("Products"); setNewProduct((current) => ({ ...current, product_type: "apparel" })); }} className="btn-primary w-full justify-between" data-testid="dashboard-add-product">
                <span className="inline-flex items-center gap-2"><Plus size={16} /> Add Product</span><ArrowRight size={15} />
              </button>
              <button type="button" onClick={() => { setTab("Gifts"); setNewProduct((current) => ({ ...current, product_type: "gift" })); }} className="btn-outline-pmt w-full justify-between" data-testid="dashboard-add-gift">
                <span className="inline-flex items-center gap-2"><Gift size={16} /> Add Gift</span><ArrowRight size={15} />
              </button>
              <button type="button" onClick={() => setTab("Custom Orders")} className="btn-outline-pmt w-full justify-between" data-testid="dashboard-manual-order">
                <span className="inline-flex items-center gap-2"><ClipboardList size={16} /> Manual Order</span><ArrowRight size={15} />
              </button>
              <button type="button" onClick={() => setTab("Orders")} className="btn-outline-pmt w-full justify-between" data-testid="dashboard-view-orders">
                <span className="inline-flex items-center gap-2"><Truck size={16} /> View Orders</span><ArrowRight size={15} />
              </button>
            </div>
          </section>

          <section className="card-pmt p-4 sm:p-5" aria-labelledby="admin-recent-orders-title" data-testid="admin-recent-orders">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="overline">Latest activity</div>
                <h2 id="admin-recent-orders-title" className="mt-1 text-xl font-bold">Recent orders</h2>
                <p className="mt-1 text-sm text-stone-600">Latest recorded orders and their current payment and fulfilment states.</p>
              </div>
              <button type="button" onClick={() => setTab("Orders")} className="btn-outline-pmt self-start sm:self-auto" data-testid="dashboard-all-orders">
                View all orders <ArrowRight size={15} />
              </button>
            </div>

            {dashboardRecentOrders.length === 0 ? (
              <div className="mt-4 rounded-lg border border-dashed border-[#E6DFD5] px-4 py-10 text-center">
                <Package size={24} className="mx-auto text-stone-400" aria-hidden="true" />
                <p className="mt-3 text-sm font-semibold">No matching orders yet</p>
                <p className="mt-1 text-xs text-stone-500">New paid orders, COD orders and manually entered orders will appear here.</p>
              </div>
            ) : (
              <>
                <div className="mt-4 space-y-3 md:hidden">
                  {[...dashboardRecentOrders]
                    .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
                    .slice(0, 5)
                    .map((order) => (
                      <article key={order.order_number} className="rounded-lg border border-[#E6DFD5] p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-mono text-xs font-bold">{order.order_number || "Order"}</div>
                            <div className="mt-1 break-words text-sm font-semibold">{order.full_name || "Customer"}</div>
                            <div className="mt-1 text-xs text-stone-500">{order.created_at ? new Date(order.created_at).toLocaleString("en-IN") : "Date unavailable"}</div>
                          </div>
                          <div className="shrink-0 text-right font-bold">{formatOrderAmount(order)}</div>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="chip">{formatPaymentStatus(order.payment_status)}</span>
                          <span className="chip">{normalizeOrderStatus(order.status).replace(/_/g, " ")}</span>
                        </div>
                      </article>
                    ))}
                </div>
                <div className="mt-4 hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[680px] text-left text-sm">
                    <thead className="bg-[#F3EDE2] text-xs uppercase text-stone-500">
                      <tr>
                        <th className="px-3 py-3">Order</th>
                        <th className="px-3 py-3">Customer</th>
                        <th className="px-3 py-3">Date</th>
                        <th className="px-3 py-3">Amount</th>
                        <th className="px-3 py-3">Payment</th>
                        <th className="px-3 py-3">Order Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...dashboardRecentOrders]
                        .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
                        .slice(0, 5)
                        .map((order) => (
                          <tr key={order.order_number} className="border-t border-[#E6DFD5]">
                            <td className="px-3 py-3 font-mono text-xs font-semibold">{order.order_number || "—"}</td>
                            <td className="px-3 py-3">
                              <div className="font-semibold">{order.full_name || "Customer"}</div>
                              <div className="mt-1 text-xs text-stone-500">{order.mobile || ""}</div>
                            </td>
                            <td className="px-3 py-3 text-xs text-stone-600">{order.created_at ? new Date(order.created_at).toLocaleDateString("en-IN") : "—"}</td>
                            <td className="px-3 py-3 font-semibold">{formatOrderAmount(order)}</td>
                            <td className="px-3 py-3"><span className="chip">{formatPaymentStatus(order.payment_status)}</span></td>
                            <td className="px-3 py-3"><span className="chip">{normalizeOrderStatus(order.status).replace(/_/g, " ")}</span></td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        </div>
      )}

      {/* NOTIFICATIONS */}
      {tab === "Notifications" && (
        <AdminNotifications
          notifications={notifications}
          markAllRead={markAllRead}
          markRead={markRead}
        />
      )}
      {/* ORDERS */}
      {tab === "Orders" && (
        <AdminOrders
          filteredOrders={filteredOrders}
          orderSearch={orderSearch}
          setOrderSearch={setOrderSearch}
          setOrderStatus={setOrderStatus}
          orderSocialMedia={orderSocialMedia}
          orderStatuses={ORDER_STATUSES}
          formatPaymentStatus={formatPaymentStatus}
          formatOrderAmount={formatOrderAmount}
          createEkartShipment={createEkartShipment}
        />
      )}

      {/* SALES */}
      {tab === "Sales" && (
        <AdminSales
          filteredSalesOrders={filteredSalesOrders}
          salesDatePreset={salesDatePreset}
          setSalesDatePreset={setSalesDatePreset}
          salesStartDate={salesStartDate}
          setSalesStartDate={setSalesStartDate}
          salesEndDate={salesEndDate}
          setSalesEndDate={setSalesEndDate}
          salesSource={salesSource}
          setSalesSource={setSalesSource}
          salesPayment={salesPayment}
          setSalesPayment={setSalesPayment}
          salesStatus={salesStatus}
          setSalesStatus={setSalesStatus}
          orderStatuses={ORDER_STATUSES}
          salesTotal={salesTotal}
          salesPaid={salesPaid}
          salesPending={salesPending}
          salesOrderCount={salesOrderCount}
          salesItemCount={salesItemCount}
          cancelledSales={cancelledSales}
          sourceBreakdown={sourceBreakdown}
          paymentBreakdown={paymentBreakdown}
          onlineSales={onlineSales}
          manualSales={manualSales}
          productSales={productSales}
        />
      )}

      {/* CUSTOM ORDERS */}
      {tab === "Custom Orders" && (
        <AdminCustomOrders
          customOrder={customOrder}
          setCustomOrder={setCustomOrder}
          manualItems={manualItems}
          updateManualItem={updateManualItem}
          addManualItem={addManualItem}
          removeManualItem={removeManualItem}
          manualSubtotal={manualSubtotal}
          manualGrandTotal={manualGrandTotal}
          addCustomOrder={addCustomOrder}
          orders={orders}
          manualOrders={manualOrders}
          totalSales={totalSales}
          stats={stats}
          setOrderStatus={setOrderStatus}
          orderStatuses={ORDER_STATUSES}
          waLink={waLink}
        />
      )}

      {/* =========================
          DESIGNS
      ========================= */}

      {tab === "Designs" && (
        <AdminDesigns
          newDesign={newDesign}
          setNewDesign={setNewDesign}
          addDesign={addDesign}
          designs={designs}
          editDesignId={editDesignId}
          setEditDesignId={setEditDesignId}
          editDesign={editDesign}
          setEditDesign={setEditDesign}
          saveEditDesign={saveEditDesign}
          startEditDesign={startEditDesign}
          delDesign={delDesign}
          toggleDesign={toggleDesign}
          designGroups={DESIGN_GROUPS}
          designCats={DESIGN_CATS}
          designGroup={designGroup}
          setDesignGroup={handleDesignGroupChange}
          designPage={designPage}
          setDesignPage={setDesignPage}
          designSearch={designSearch}
          setDesignSearch={handleDesignSearchChange}
          designCat={designCat}
          setDesignCat={handleDesignCategoryChange}
          statusFilter={designStatusFilter}
          setStatusFilter={handleDesignStatusChange}
          handleUpload={handleUpload}
          handleFileDrop={handleFileDrop}
          handleImageArrayUpload={handleImageArrayUpload}
          removeImageAt={removeImageAt}
          handleImageArrayFile={handleImageArrayFile}
          handleUploadFile={handleUploadFile}
        />
      )}

      {/* =========================
          PRODUCTS
      ========================= */}

      {(tab === "Products" || tab === "Gifts") && (
        <>
        {tab === "Gifts" && (
          <div className="mb-4 border border-[#E6DFD5] bg-white p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider">Gift Products</h2>
            <p className="mt-1 text-sm text-gray-600">Manage gifts separately from apparel products. Gift personalisation fields are available in the product form.</p>
            <div className="mt-3 border border-[#E6DFD5] bg-[#F8F4ED] p-3 text-sm" data-testid="gift-delivery-rules">
              <p className="font-semibold">Shared delivery rules</p>
              <p className="mt-1 text-gray-600">Orders below ₹1,000: ₹50 delivery for gift-only orders when no design delivery charge applies. Orders of ₹1,000 or more: free delivery. Mixed orders use the existing design delivery charge. Delivery is calculated once per order, not per gift.</p>
            </div>
            <AdminProductBulkImport onImported={() => { setProductStatusFilter("all"); return loadProducts(true, "all"); }} />
          </div>
        )}
        <AdminProducts
          newProduct={newProduct}
          setNewProduct={(value) => setNewProduct((current) => {
            const next = typeof value === "function" ? value(current) : value;
            return { ...next, product_type: tab === "Gifts" ? "gift" : (next.product_type === "gift" ? "apparel" : next.product_type) };
          })}
          addProduct={addProduct}
          products={products.filter((product) => tab === "Gifts" ? product.product_type === "gift" : product.product_type !== "gift")}
          renderDashboardPricingPreview={renderDashboardPricingPreview}
          statusFilter={productStatusFilter}
          setStatusFilter={setProductStatusFilter}
          editProduct={editProduct}
          setEditProduct={setEditProduct}
          setEditProductSlug={setEditProductSlug}
          startEditProduct={startEditProduct}
          saveEditProduct={saveEditProduct}
          toggleProduct={toggleProduct}
          delProduct={delProduct}
          handleUpload={handleUpload}
          handleFileDrop={handleFileDrop}
          handleImageArrayUpload={handleImageArrayUpload}
          removeImageAt={removeImageAt}
          handleImageArrayFile={handleImageArrayFile}
          handleUploadFile={handleUploadFile}
          handleVariantImageFile={handleVariantImageFile}
          handleVariantImageUpload={handleVariantImageUpload}
          handleVariantImageDrop={handleVariantImageDrop}
          removeVariantImage={removeVariantImage}
          variantImageFor={variantImageFor}
          fixedProductType={tab === "Gifts" ? "gift" : "apparel"}
        />
        </>
      )}


      {/* SIZE CHARTS */}
      {tab === "Size Charts" && (
        <AdminSizeCharts
          products={products}
          loading={loading}
          onSave={saveSizeChart}
        />
      )}

      {/* OFFERS */}
      {tab === "Offers" && (
        <AdminOffers
          offerForm={offerForm}
          setOfferForm={setOfferForm}
          offers={offers}
          savingOffer={savingOffer}
          saveOffer={saveOffer}
          resetOfferForm={resetOfferForm}
          editOffer={editOffer}
          toggleOffer={toggleOffer}
          deleteOffer={deleteOffer}
        />
      )}

      {/* SETTINGS */}
      {tab === "Settings" && (
        <AdminSettings
          customPricing={customPricing}
          updateCustomPrice={updateCustomPrice}
          updatePersonalisationPrice={updatePersonalisationPrice}
          saveCustomPricing={saveCustomPricing}
          savingCustomPricing={savingCustomPricing}
          codEnabled={codEnabled}
          savingCodEnabled={savingCodEnabled}
          saveCodEnabled={saveCodEnabled}
        />
      )}
    </div>
  );
}